import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { siteInfo } from '@/mocks/comics';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setFormError('请填写所有必填字段');
      setFormStatus('error');
      return;
    }
    setFormStatus('success');
    setFormError('');
    setContactForm({ name: '', email: '', message: '' });
    setTimeout(() => setFormStatus('idle'), 3000);
  };

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-line' },
    { key: 'reputation' as const, label: '用户口碑', icon: 'ri-heart-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-mail-line' },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        email: siteInfo.email,
        foundingDate: siteInfo.established,
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 韩漫网',
        url: `${siteInfo.url}/about`,
        isPartOf: {
          '@type': 'WebSite',
          name: siteInfo.name,
          url: siteInfo.url,
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '韩漫网的漫画是否免费？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '是的，韩漫网所有韩国漫画内容均为免费阅读，无需付费，注册即可享受全部漫画资源。',
            },
          },
          {
            '@type': 'Question',
            name: '韩漫网支持哪些设备阅读？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '韩漫网采用响应式设计，支持电脑、平板和手机等多种设备，随时随地享受漫画阅读乐趣。',
            },
          },
          {
            '@type': 'Question',
            name: '漫画内容是否为无删减版本？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '韩漫网所有漫画内容均为完整的无删减版本，保留原作的完整故事情节和画面。',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们_韩漫网_免费韩漫在线阅读平台"
        description="韩漫网品牌介绍与联系方式。韩漫网致力于提供最优质的免费韩国漫画阅读体验，海量无删减漫画下拉式在线观看，服务超过30万日活用户。"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-24 pb-12 min-h-screen">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>

          <div className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              关于韩漫网
            </h1>
            <p className="text-foreground-600 max-w-2xl">
              韩漫网成立于{siteInfo.established}年，是国内领先的韩国漫画免费在线阅读平台，致力于为漫迷提供最优质的阅读体验。
            </p>
          </div>

          <div className="flex flex-wrap gap-1 mb-10 bg-background-100 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-background-50 text-primary-700 shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'intro' && (
            <div className="space-y-8">
              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8 md:p-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">
                  <i className="ri-building-2-line text-primary-500 mr-2"></i>
                  品牌故事
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      <strong className="text-foreground-900">韩漫网</strong>成立于{siteInfo.established}年，由一群热爱韩国漫画的年轻人共同创建。我们深知漫迷对优质漫画内容的渴望，因此倾力打造了这个汇聚海量<strong className="text-foreground-900">免费韩漫大全</strong>的阅读平台。
                    </p>
                    <p>
                      平台上线至今，已收录超过<strong className="text-foreground-900">{siteInfo.comicCount.toLocaleString()}+ 部韩国漫画</strong>作品，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、耽美、百合等多种类型，满足不同漫迷的阅读需求。
                    </p>
                    <p>
                      所有漫画内容均为<strong className="text-foreground-900">无删减漫画</strong>版本，采用先进的<strong className="text-foreground-900">下拉式在线观看</strong>技术，为您带来流畅、沉浸式的阅读体验。
                    </p>
                  </div>
                  <div className="rounded-xl overflow-hidden h-64 lg:h-auto">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20cozy%20modern%20creative%20studio%20with%20comic%20books%20and%20illustrations%20on%20walls%2C%20warm%20ambient%20lighting%2C%20wooden%20desk%20with%20drawing%20tablet%2C%20Korean%20manhwa%20art%20displayed%2C%20creative%20workspace%20atmosphere%2C%20comfortable%20and%20inspiring%20environment&width=800&height=600&seq=about-brand-story&orientation=landscape"
                      alt="韩漫网品牌故事"
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-focus-3-line text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    打破漫画阅读的壁垒，让每一位漫迷都能免费享受优质的韩国漫画内容。
                  </p>
                </div>
                <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-eye-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    成为全球最大的中文韩国漫画阅读平台，连接创作者与漫迷的美好桥梁。
                  </p>
                </div>
                <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-shield-check-line text-2xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">我们的承诺</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    坚持提供无删减、高品质的漫画内容，持续优化阅读体验，保护用户隐私安全。
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reputation' && (
            <div className="space-y-8">
              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8 md:p-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">
                  <i className="ri-heart-3-line text-accent-500 mr-2"></i>
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                  <div className="text-center p-6 rounded-xl bg-background-100">
                    <p className="text-4xl font-bold text-accent-500 font-heading mb-2">{(siteInfo.dailyUsers / 10000).toFixed(0)}万+</p>
                    <p className="text-sm text-foreground-600">日活用户</p>
                  </div>
                  <div className="text-center p-6 rounded-xl bg-background-100">
                    <p className="text-4xl font-bold text-primary-500 font-heading mb-2">{(siteInfo.totalViews / 10000000).toFixed(0)}千万+</p>
                    <p className="text-sm text-foreground-600">总阅读量</p>
                  </div>
                  <div className="text-center p-6 rounded-xl bg-background-100">
                    <p className="text-4xl font-bold text-accent-500 font-heading mb-2">4.9</p>
                    <p className="text-sm text-foreground-600">用户平均评分</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      name: '漫画迷小王',
                      comment: '韩漫网的漫画资源真的超级丰富！我最喜欢她的秘密花园和命运轮回这两部，画面精美，剧情引人入胜，而且真的是无删减版本，阅读体验一流！',
                      rating: 5,
                      date: '2026-08-01',
                    },
                    {
                      name: '韩剧控小美',
                      comment: '自从发现了韩漫网，每天都要上来追更。下拉式阅读太方便了，不用翻页等待加载，一口气能看好几话。强烈推荐给所有喜欢韩国漫画的朋友！',
                      rating: 5,
                      date: '2026-07-28',
                    },
                    {
                      name: '二次元阿杰',
                      comment: '作为老漫迷，我对比过很多漫画平台，韩漫网无论是内容丰富度还是阅读体验都是最好的。分类也很清晰，找漫画特别方便。',
                      rating: 5,
                      date: '2026-07-25',
                    },
                    {
                      name: '追漫达人小陈',
                      comment: '韩漫网的更新速度很快，很多热门漫画都能第一时间看到最新话。而且完全免费，这点真的太良心了！希望韩漫网越做越好！',
                      rating: 5,
                      date: '2026-07-20',
                    },
                  ].map((review) => (
                    <div key={review.name} className="bg-background-100 rounded-xl p-6">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        &ldquo;{review.comment}&rdquo;
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground-900">{review.name}</span>
                        <span className="text-xs text-foreground-400">{review.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                    <i className="ri-mail-send-line text-primary-500 mr-2"></i>
                    发送消息
                  </h2>
                  <form onSubmit={handleFormSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      />
                      <p className="text-xs text-foreground-400 mt-1">{contactForm.message.length}/500</p>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />

                    <button
                      type="submit"
                      className="w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                    >
                      <i className="ri-send-plane-line"></i>
                      发送消息
                    </button>

                    {formStatus === 'success' && (
                      <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-4 py-2.5 rounded-lg">
                        <i className="ri-check-line"></i>
                        消息发送成功！我们会尽快回复您。
                      </div>
                    )}
                    {formStatus === 'error' && formError && (
                      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg">
                        <i className="ri-error-warning-line"></i>
                        {formError}
                      </div>
                    )}
                  </form>
                </div>

                <div className="space-y-6">
                  <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                      <i className="ri-contacts-line text-accent-500 mr-2"></i>
                      联系方式
                    </h2>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                          <i className="ri-mail-line text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                          <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                          <i className="ri-timer-line text-accent-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">工作时间</p>
                          <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                          <i className="ri-message-3-line text-secondary-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">反馈建议</p>
                          <p className="text-sm text-foreground-600">如有任何问题或建议，欢迎随时联系我们，我们将尽快回复。</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                      <i className="ri-question-answer-line text-primary-500 mr-2"></i>
                      常见问题
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                          <i className="ri-question-line text-primary-500 mr-1"></i>
                          韩漫网的漫画是否免费？
                        </h3>
                        <p className="text-sm text-foreground-600 ml-5">
                          是的，韩漫网所有韩国漫画内容均为免费阅读，无需付费，注册即可享受全部漫画资源。
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                          <i className="ri-question-line text-primary-500 mr-1"></i>
                          韩漫网支持哪些设备阅读？
                        </h3>
                        <p className="text-sm text-foreground-600 ml-5">
                          韩漫网采用响应式设计，支持电脑、平板和手机等多种设备，随时随地享受漫画阅读乐趣。
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                          <i className="ri-question-line text-primary-500 mr-1"></i>
                          漫画内容是否为无删减版本？
                        </h3>
                        <p className="text-sm text-foreground-600 ml-5">
                          韩漫网所有漫画内容均为完整的无删减版本，保留原作的完整故事情节和画面。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}